/**
 * §4.3's statement: the document a merchant hands to their own bookkeeper.
 *
 * Shown on screen before it is downloaded, deliberately. A merchant who is
 * disputing a charge wants to find the line, not to download a PDF, open it,
 * find the line, and then come back — and a screen that can only produce files
 * is one a shopkeeper on a phone with no PDF reader cannot use at all.
 *
 * The running balance is the server's, not this component's. It would be a
 * two-line reduce here and it must not be: §4.3 requires the statement to come
 * from the ledger, and a client that recomputed it would eventually disagree
 * with the PDF over the same period.
 */

import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useState } from "react";

import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Pill,
  SkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TextField,
} from "@/components/ui";
import { formatDate, formatMoney } from "@/lib/format";
import {
  premiumBillingRepository,
  type Statement,
} from "@/repositories/premiumBillingRepository";

export function PremiumStatements() {
  const [range, setRange] = useState(currentMonth);
  const [downloading, setDownloading] = useState<"pdf" | "csv" | null>(null);

  const statement = useQuery({
    queryKey: ["premium", "statement", range.from, range.to],
    queryFn: () => premiumBillingRepository.statement(range),
  });

  async function download(format: "pdf" | "csv") {
    setDownloading(format);
    try {
      const blob = await premiumBillingRepository.downloadStatement({
        from_date: range.from,
        to_date: range.to,
        format,
      });
      saveBlob(
        blob,
        `smartspaza-statement-${range.from}-${range.to}.${format}`,
      );
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div className="space-y-5">
      <Card className="flex flex-wrap items-end gap-3">
        <TextField
          label="From"
          type="date"
          value={range.from}
          onChange={(event) => setRange({ ...range, from: event.target.value })}
          className="w-40"
        />
        <TextField
          label="To"
          type="date"
          value={range.to}
          onChange={(event) => setRange({ ...range, to: event.target.value })}
          className="w-40"
        />

        <div className="ml-auto flex gap-2">
          <Button
            icon={<Download className="size-4" />}
            loading={downloading === "pdf"}
            onClick={() => void download("pdf")}
          >
            PDF
          </Button>
          {/* CSV is for reconciliation, not for reading — §4.3 — so it is the
              quieter of the two buttons rather than an equal choice. */}
          <Button
            variant="ghost"
            loading={downloading === "csv"}
            onClick={() => void download("csv")}
          >
            CSV
          </Button>
        </div>
      </Card>

      {statement.isError ? (
        <ErrorState
          title="That statement could not be produced"
          onRetry={() => statement.refetch()}
        />
      ) : statement.isLoading || !statement.data ? (
        <SkeletonRows rows={8} />
      ) : (
        <StatementTable statement={statement.data} />
      )}
    </div>
  );
}

function StatementTable({ statement }: { statement: Statement }) {
  if (statement.lines.length === 0) {
    return (
      <Card>
        <EmptyState
          title="Nothing in this period"
          description={`The balance was ${formatMoney(
            centsToRand(statement.opening_cents),
          )} throughout.`}
        />
      </Card>
    );
  }

  return (
    <Card flush className="p-5">
      <Table>
        <THead>
          <TH>Date</TH>
          <TH>Description</TH>
          <TH hideBelow="lg">Reference</TH>
          <TH hideBelow="sm" className="text-right">
            Debit
          </TH>
          <TH hideBelow="sm" className="text-right">
            Credit
          </TH>
          <TH className="text-right">Balance</TH>
        </THead>
        <TBody>
          <TR>
            <TD className="text-sm text-ink-500">
              {formatDate(statement.from_date)}
            </TD>
            <TD className="text-sm italic text-ink-500">Opening balance</TD>
            <TD hideBelow="lg">{""}</TD>
            <TD hideBelow="sm">{""}</TD>
            <TD hideBelow="sm">{""}</TD>
            <TD className="text-right tabular-nums text-ink-600">
              {formatMoney(centsToRand(statement.opening_cents))}
            </TD>
          </TR>

          {statement.lines.map((line, index) => (
            <TR key={`${line.reference}-${index}`}>
              <TD className="text-sm text-ink-500">
                {formatDate(line.service_date)}
              </TD>
              <TD className="text-sm text-ink-900">
                {line.description}
                {line.status === "REVERSED" && (
                  <Pill tone="neutral" className="ml-2">
                    Reversed
                  </Pill>
                )}
              </TD>
              <TD hideBelow="lg" className="font-mono text-xs text-ink-500">
                {line.reference}
              </TD>
              <TD
                hideBelow="sm"
                className="text-right tabular-nums text-ink-900"
              >
                {line.debit_cents
                  ? formatMoney(centsToRand(line.debit_cents))
                  : "—"}
              </TD>
              <TD
                hideBelow="sm"
                className="text-right tabular-nums text-money-700"
              >
                {line.credit_cents
                  ? formatMoney(centsToRand(line.credit_cents))
                  : "—"}
              </TD>
              <TD className="text-right tabular-nums text-ink-600">
                {formatMoney(centsToRand(line.balance_cents))}
              </TD>
            </TR>
          ))}

          <TR>
            <TD className="text-sm text-ink-500">
              {formatDate(statement.to_date)}
            </TD>
            <TD className="text-sm font-medium text-ink-900">
              Closing balance
            </TD>
            <TD hideBelow="lg">{""}</TD>
            <TD hideBelow="sm" className="text-right font-medium tabular-nums">
              {formatMoney(centsToRand(statement.total_debits_cents))}
            </TD>
            <TD hideBelow="sm" className="text-right font-medium tabular-nums">
              {formatMoney(centsToRand(statement.total_credits_cents))}
            </TD>
            <TD className="text-right font-medium tabular-nums">
              {formatMoney(centsToRand(statement.closing_cents))}
            </TD>
          </TR>
        </TBody>
      </Table>
    </Card>
  );
}

/**
 * Cents to a decimal string, for `formatMoney`.
 *
 * Through the string rather than by dividing: `9007199254740993 / 100` is not a
 * problem a statement will ever hit, but `.toFixed(2)` on a float is how a cent
 * goes missing on a document somebody's accountant checks.
 */
function centsToRand(cents: number): string {
  const negative = cents < 0;
  const digits = String(Math.abs(cents)).padStart(3, "0");
  const value = `${digits.slice(0, -2)}.${digits.slice(-2)}`;
  return negative ? `-${value}` : value;
}

function currentMonth(): { from: string; to: string } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from: isoDate(first), to: isoDate(now) };
}

function isoDate(value: Date): string {
  return value.toISOString().slice(0, 10);
}

/**
 * Hand the file to the browser.
 *
 * An object URL and a synthetic click, revoked immediately after. The download
 * comes back as a blob because it is an authenticated request — a plain link to
 * the endpoint would arrive without the access token and answer 401.
 */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
